import Layout from '@/components/Layout/Layout';
import BackButton from '@/components/ui/BackButton';
import FieldLayout from '@/components/Layout/FieldLayout';
import PrimaryInput from '@/components/ui/PrimaryInput';
import { useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import moment from 'moment';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ViewDocumentModal from '@/components/common/ViewDocumentModal';
import {
  getDateOnly,
  getFilterUrl,
  showToastNotification,
} from '@/utility/helper';
import FormButtons from '@/components/common/Form/FormButtons';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IGetLeaveCountResponse,
  ILeaveCountResponse,
  IRemainingLeaveItem,
  IRemainingLeaveResponse,
} from './MyLeave';
import { IAddLeaveForm, IFileState } from '@/interfaces/leave.model';
import { IDeleteResponse } from '@/interfaces/common.model';
import { RoutesLink } from '@/utility/RouteLinks';
import { ILeaveDetailResponse } from './LeaveDetail';
import Label from '@/components/ui/Label';
import {
  calculateDaysDifference,
  formatNumber,
} from '@/utility/CommonFunctions';
import FullPageLoader from '@/components/loader/FullPageLoader';
import { IShift, IShiftResponse } from '@/interfaces/Shits.model';
interface IEditLeaveData {
  id: string;
  startDate1: string;
  endDate1: string;
  leaveType: string;
  reason: string;
  leaveDuration: string;
  url: File | null;
}
interface IAddLeavePageState {
  EditLeaveData: IEditLeaveData | null;
  userName: null;
  listType: IRemainingLeaveItem[];
  sortBySingleDate: string[];
  userId: string | null;
  detailLoader: boolean;
}
export default function AddLeave() {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location?.state;
  const state = location?.state;
  const [isLoading, setIsLoading] = useState(false);
  const [pageState, setPageState] = useState<IAddLeavePageState>({
    EditLeaveData: null,
    userName: null,
    listType: [],
    sortBySingleDate: [],
    userId: null,
    detailLoader: false,
  });
  const [date, setDate] = useState({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });
  const [file, setFile] = useState<IFileState>({
    file: null,
    imageUrl: null,
    deleteUrl: null,
    isDelete: false,
    isHalfday: false,
  });
  const [shifts, setShifts] = useState<IShift[] | null>(null);
  const [modal, setModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const toggleModal = () => setModal(!modal);
  const isAdminAddingForUser = Boolean(
    state?.userId || state?.markAttendance || state?.source,
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<IAddLeaveForm>();
  const type = watch('leaveType');
  const getShifts = useCallback(async () => {
    try {
      const response: IShiftResponse = await callApi(
        API_URL + API_ENDPOINT.getShift,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        setShifts(response?.data?.shift);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  }, []);

  const getLeaveCount = useCallback(
    async (type?: string) => {
      try {
        const leaveType = type ?? state?.type ?? 'remaining';

        const filter = {
          type: leaveType,
          userId: state?.userId,
        };

        const url =
          API_URL +
          API_ENDPOINT.getLeaveCount +
          '?' +
          getFilterUrl(filter) +
          '&';

        const res: IGetLeaveCountResponse = await callApi(url, {
          method: 'GET',
        });

        if (!res?.status) {
          showToastNotification(
            'danger',
            res?.message || 'Something went wrong',
          );
          return;
        }

        if (leaveType === 'remaining') {
          const items = (res as IRemainingLeaveResponse).data;
          setPageState((prev) => ({
            ...prev,
            listType: items,
          }));
        } else {
          const countData = (res as ILeaveCountResponse).data;
          setPageState((prev) => ({
            ...prev,
            leaveCount: countData,
          }));
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
      }
    },
    [state?.type, state?.userId],
  );

  const getLeaveDetail = useCallback(async () => {
    try {
      const API =
        API_URL + API_ENDPOINT?.getLeaveDetail + `?leaveId=${data?.id}`;
      setPageState((prev) => ({
        ...prev,
        detailLoader: true,
      }));
      const response: ILeaveDetailResponse = await callApi(API, {
        method: 'GET',
      });
      setPageState((prev) => ({
        ...prev,
        detailLoader: false,
      }));
      if (response?.status === true) {
        reset({
          leaveType: response?.data?.leaveType,
          reason: response?.data?.reason,
          halfDayLeave: response?.data?.leaveDuration,
        });
        setFile((prev) => ({
          ...prev,
          imageUrl: response?.data?.url,
          deleteUrl: response?.data?.url,
          isHalfday: response?.data?.leaveDuration !== 'fullDay' ? true : false,
        }));
        setDate({
          startDate: getDateOnly(response?.data?.startDate),
          endDate: getDateOnly(response?.data?.endDate),
        });
        if (response?.data?.leaveDuration === 'fullDay') {
          setValue('fullDay', 'Full');
          setValue('halfDayOption', '');
          setFile((prev) => ({ ...prev, isHalfday: false }));
        }

        if (response?.data?.leaveDuration === 'firstHalf') {
          setValue('fullDay', '');
          setValue('halfDayOption', 'firstHalf');
          setFile((prev) => ({ ...prev, isHalfday: true }));
        }
        if (response?.data?.leaveDuration === 'secondHalf') {
          setValue('fullDay', '');
          setValue('halfDayOption', 'secondHalf');
          setFile((prev) => ({ ...prev, isHalfday: true }));
        }
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageState((prev) => ({
        ...prev,
        detailLoader: false,
      }));
    }
  }, [data?.id, reset, setValue]);

  useEffect(() => {
    getShifts();
  }, [getShifts]);

  useEffect(() => {
    setValue('startDate', moment().format('YYYY-MM-DD'));
  }, [setValue]);

  const onSubmit: SubmitHandler<IAddLeaveForm> = async (data) => {
    try {
      const postData = {
        ...(state?.id && { leaveId: state?.id }),
        ...(state?.userId && { userId: state?.userId }),
        leaveType: data.leaveType,
        startDate: date.startDate,
        endDate: date.endDate,
        reason: data.reason,
        url: file.file,
        ...(file?.isDelete && { deleteDocument: file?.deleteUrl }),
        ...(file?.isHalfday
          ? {
              ...(data?.halfDayOption === 'secondHalf'
                ? {
                    isHlafDayRequest: true,
                    leaveDuration: data?.halfDayOption,
                    checkInTime: shifts?.find(
                      (shift) => shift.shiftName === 'Second-half',
                    )?.startTime,
                    checkOutTime: shifts?.find(
                      (shift) => shift.shiftName === 'Second-half',
                    )?.endTime,
                  }
                : {
                    isHlafDayRequest: true,
                    leaveDuration: data?.halfDayOption,
                    checkInTime: shifts?.find(
                      (shift) => shift.shiftName === 'First-half',
                    )?.startTime,
                    checkOutTime: shifts?.find(
                      (shift) => shift.shiftName === 'First-half',
                    )?.endTime,
                  }),
            }
          : { leaveDuration: 'fullDay' }),
        ...(state?.markAttendance && { from: 'markAttendance' }),
      };

      const formData = new FormData();
      Object.keys(postData).forEach((key) => {
        const value = postData[key];
        if (value) {
          formData.append(key, value);
        }
      });
      setIsLoading(true);
      const API = state?.id
        ? API_URL + API_ENDPOINT.updateLeave
        : API_URL + API_ENDPOINT.addLeave;
      const res: IDeleteResponse = await callApi(API, {
        method: 'POST',
        data: formData,
        multipart: true,
      });
      setIsLoading(false);
      if (res?.status) {
        showToastNotification('success', res?.message);
        if (state?.source === 'teamAttendance') {
          navigate(RoutesLink.teamAttendance);
        } else if (state?.source === 'manageBulk') {
          navigate(RoutesLink.manageBulkAttendance);
        } else {
          navigate(RoutesLink.myLeave);
        }

        reset();
        setFile({
          file: null,
          imageUrl: null,
          deleteUrl: null,
          isDelete: false,
          isHalfday: false,
        });
      } else {
        showToastNotification('danger', res?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!data?.data?.userId) {
      getLeaveCount('remaining');
    } else {
      getLeaveCount();
    }
    if (data?.id) {
      getLeaveDetail();
    }
  }, [data?.data?.userId, data?.id, getLeaveCount, getLeaveDetail]);
  useEffect(() => {
    if (location?.state !== null) {
      const startDate = data?.pageStates?.sortBySingleDate[0];
      const endDate = data?.pageStates?.sortBySingleDate[1];
      setDate({
        startDate: startDate,
        endDate: endDate,
      });
    } else {
      reset();
    }
  }, [data?.pageStates?.sortBySingleDate, location?.state, reset]);

  useEffect(() => {
    if (state?.userId) {
      setPageState((prev) => ({
        ...prev,
        userId: state.userId,
        userName: state.userName,
        sortBySingleDate: state.sortBySingleDate ?? [],
      }));
      getLeaveCount('remaining');
    }

    if (state?.markAttendance) {
      setDate({
        startDate: state.sortBySingleDate?.[0] ?? moment().format('YYYY-MM-DD'),
        endDate: state.sortBySingleDate?.[0] ?? moment().format('YYYY-MM-DD'),
      });
    }

    if (state?.id) {
      getLeaveDetail();
    }
  }, [
    state?.id,
    state?.markAttendance,
    state?.sortBySingleDate,
    state?.userId,
    state?.userName,
    getLeaveCount,
    getLeaveDetail,
    location?.state,
    reset,
  ]);

  return (
    <Layout>
      {pageState?.detailLoader ? (
        <FullPageLoader />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row align-items-center row-gap-3">
            <BackButton
              isEdit={state?.id ? true : false}
              isDetail={!state?.id}
              label={
                state?.id
                  ? ' Leave'
                  : state?.userName
                    ? `Applying Leave For ${state?.userName}`
                    : 'Apply for Leave'
              }
            />

            <div className="card">
              <div className="card-body">
                <div className="col-12 mb-4 mt-3">
                  <div className="row">
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <Label required={true} id="leaveType">
                        Leave Type
                      </Label>
                      {/* <label className="form-label d-flex">
                      Leave Type <span className="text-danger">*</span>
                    </label> */}
                      <select
                        className="form-select"
                        key={watch('leaveType')}
                        {...register('leaveType', {
                          required: 'Leave type is required',
                        })}
                      >
                        <option value="">Select leave type</option>
                        {pageState?.listType?.map((item, i) => (
                          <option key={i} value={item.leaveType}>
                            {item.leaveType}
                          </option>
                        ))}
                      </select>
                      {errors.leaveType && (
                        <small className="text-danger">
                          {errors.leaveType.message}
                        </small>
                      )}
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <div className="form-group h-100  mt-2">
                        <label htmlFor="leave" className="d-flex gap-2">
                          <span className="day-type-size">Day Type</span>
                          <span
                            className="ms-auto"
                            style={{
                              color: '#6B6B6B',
                              fontSize: '14px',
                            }}
                          >
                            Remaining:{' '}
                            <b className="text-primary">
                              {(() => {
                                if (type) {
                                  const selectedLeave = pageState.listType.find(
                                    (item) => item.leaveType === type,
                                  );
                                  return selectedLeave
                                    ? formatNumber(
                                        +selectedLeave.remainingLeave,
                                      )
                                    : 0;
                                } else {
                                  const totalRemaining =
                                    pageState.listType?.reduce(
                                      (acc, item) =>
                                        acc + Number(item.remainingLeave),
                                      0,
                                    ) || 0;
                                  return formatNumber(totalRemaining);
                                }
                              })()}
                            </b>
                          </span>
                        </label>

                        <div className="d-flex gap-2 gap-lg-2 mt-2 radio-color flex-wrap">
                          <div className="form-group ">
                            <label className="d-flex gap-2 py-2 ">
                              <input
                                type="radio"
                                value="Full"
                                className="input-radio"
                                {...register('fullDay', {
                                  onChange: () => {
                                    setFile((prev) => ({
                                      ...prev,
                                      isHalfday: false,
                                    }));
                                    setValue('halfDayOption', '');
                                  },
                                })}
                                checked={!file.isHalfday}
                              />
                              <span className="me-5 fs-4">Full</span>
                            </label>
                          </div>

                          <div className="form-group ">
                            <label className="d-flex gap-2 py-2 me-5">
                              <input
                                type="radio"
                                value="firstHalf"
                                className="input-radio"
                                {...register('halfDayOption', {
                                  onChange: (e) => {
                                    setFile((prev) => ({
                                      ...prev,
                                      isHalfday: e.target.checked,
                                    }));
                                  },
                                  required: file.isHalfday
                                    ? 'Please select First or Second Half.'
                                    : false,
                                })}
                                checked={
                                  file.isHalfday &&
                                  watch('halfDayOption') === 'firstHalf'
                                }
                              />
                              <span className="me-5 fs-4">First Half</span>
                            </label>
                          </div>

                          <div className="form-group ">
                            <label className="d-flex gap-2 py-2">
                              <input
                                type="radio"
                                value="secondHalf"
                                className="input-radio"
                                {...register('halfDayOption', {
                                  onChange: (e) => {
                                    setFile((prev) => ({
                                      ...prev,
                                      isHalfday: e.target.checked,
                                    }));
                                  },
                                })}
                                checked={
                                  file.isHalfday &&
                                  watch('halfDayOption') === 'secondHalf'
                                }
                              />
                              <span className="fs-4">Second Half</span>
                            </label>
                          </div>
                        </div>

                        {errors.halfDayOption && (
                          <div className="form-text text-danger">
                            {errors.halfDayOption.message}
                          </div>
                        )}
                      </div>
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        label="Start Date"
                        type="date"
                        required={true}
                        value={date.startDate}
                        {...register('startDate', {
                          required: 'Start date is required',
                          validate: (val) => {
                            if (moment(val).day() === 0) {
                              return 'Sunday is a non-working day.';
                            }
                            if (isAdminAddingForUser) return true;

                            return (
                              moment(val).isSameOrAfter(
                                moment().startOf('year'),
                                'day',
                              ) ||
                              'Start date cannot be before Jan 1st of this year'
                            );
                          },
                        })}
                        readOnly={state?.userName ? true : false}
                        min={
                          isAdminAddingForUser
                            ? undefined
                            : moment().startOf('year').format('YYYY-MM-DD')
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          if (moment(val).day() === 0) {
                            return;
                          }

                          setValue('startDate', val);
                          setDate({ startDate: val, endDate: val });
                        }}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-12 col-lg-6">
                      <PrimaryInput
                        label="End Date"
                        type="date"
                        required={true}
                        // defaultValue={date.startDate}
                        value={date.endDate}
                        readOnly={state?.userName ? true : false}
                        {...register('endDate', {
                          required: 'End date is required',
                          validate: (val) => {
                            if (moment(val).day() === 0) {
                              return 'Sunday is a non-working day. Please select another date.';
                            }
                            if (
                              moment(val).isAfter(
                                moment(date.startDate).endOf('week'),
                                'day',
                              )
                            ) {
                              return 'End date cannot be beyond this Saturday.';
                            }
                            return (
                              moment(val).isSameOrAfter(
                                moment(date.startDate),
                                'day',
                              ) ||
                              'End date must be same as or after start date'
                            );
                          },
                        })}
                        min={date.startDate}
                        max={moment(date.startDate)
                          .endOf('week')
                          .format('YYYY-MM-DD')}
                        onChange={(e) =>
                          setDate((prev) => ({
                            ...prev,
                            endDate: e.target.value,
                          }))
                        }
                      />
                    </FieldLayout>
                  </div>
                  <p
                    className="text-center mb-0"
                    style={{
                      color: '#6B6B6B',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {(() => {
                      const days =
                        Number(
                          calculateDaysDifference(
                            date.startDate,
                            date.endDate,
                            'number',
                          ),
                        ) || 0;
                      const result = file?.isHalfday ? days * 0.5 : days;
                      return result
                        ? `${result} ${result === 1 ? 'day' : 'days'}`
                        : '';
                    })()}
                  </p>
                  <FieldLayout fieldStyle="col-12 col-lg-6">
                    <label className="fs-4">Attachment</label>
                    {!file.imageUrl ? (
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*, .pdf"
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          if (target && target.files) {
                            const f = target.files[0];
                            const url = URL.createObjectURL(f);
                            setFile({ ...file, file: f, imageUrl: url });
                          }
                        }}
                      />
                    ) : (
                      <div>
                        <span
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedDocument(file.file);
                            toggleModal();
                          }}
                        >
                          {/* {location?.state && !file.file
                          ? file.imageUrl
                          : file?.file?.name} */}
                          {file?.imageUrl
                            ? file?.imageUrl?.split('-')[1]
                            : file?.file?.name}
                        </span>

                        <button
                          className="btn btn-danger btn-sm ms-3"
                          type="button"
                          onClick={() =>
                            setFile({
                              file: null,
                              imageUrl: null,
                              isDelete: true,
                            })
                          }
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </FieldLayout>
                  <FieldLayout fieldStyle="col-12 col-lg-6">
                    <Label id="reason" required={true}>
                      {' '}
                      Reason{' '}
                    </Label>
                    <textarea
                      className="form-control"
                      rows={3}
                      {...register('reason', {
                        required: 'Reason is required',
                      })}
                    />
                    {errors.reason && (
                      <small className="text-danger">
                        {errors.reason.message}
                      </small>
                    )}
                  </FieldLayout>
                </div>
              </div>
              <FormButtons isLoading={isLoading} buttonStyle="gap-4 my-4">
                {state?.id ? 'Update Leave' : 'Apply Leave'}
              </FormButtons>
            </div>
          </div>
        </form>
      )}
      <ViewDocumentModal
        isOpen={modal}
        toggle={toggleModal}
        selectedDocument={selectedDocument}
      />
    </Layout>
  );
}
